/**
 *
 * InvoiceDetailDetectionsFormField
 *
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FlatInvoiceDetail } from 'lib/api/flatInvoiceDetails';
import { messages } from 'core/translations/invoiceDetails';
import store from 'store';

// ui
import { Select, TextField } from '@domonda/ui';
import './styles.css';

// form
import {
  FormSelectField,
  FormDateField,
  FormInputField,
  useFormContext,
} from '@domonda/react-form';
import { FormTag } from '@domonda/form';
import { FormattedMessage, useLocale } from 'lib/intl';

//helpers
import { containsValidNumberCharacters, convertToLocale, validateNumber } from 'lib/numberHelpers';

// modules
import { OCRTextPageDetection } from 'modules/OCRTextPage';
import { FormValues } from '../InvoiceDetail/selectors';
import _ from 'lodash';
import { forceReRender } from '@storybook/react';
import { DetectedSupplier, DetectedClient } from 'lib/api/detectInvoice';
import { InvoiceAutocomplete } from 'modules/Autocomplete/Autocomplete';
import { getClients } from 'lib/api/clientData';
import { getSuppliers } from 'lib/api/supplierData';
import { ErrorCountContext } from 'lib/api/invoiceErrorCountContext';
export interface InvoiceDetailDetectionsFormFieldProps {
  refs: Record<FlatInvoiceDetail['Id'], React.MutableRefObject<{ focus: () => void } | null>>;
  flatInvoiceDetail: FlatInvoiceDetail;
  onHighlightDetection: (detection: OCRTextPageDetection | null) => void;
  disabled: boolean;
  onThreshold: (name: string, above: boolean) => void;
  readOnly?: boolean;
  autoFocus: boolean | undefined;
  additionalProperties: boolean | undefined;
  pagesWithDetections?: any;
}

const InvoiceDetailDetectionsFormField: React.FC<InvoiceDetailDetectionsFormFieldProps> = (
  props,
) => {
  const {
    refs,
    flatInvoiceDetail,
    onHighlightDetection,
    disabled,
    onThreshold,
    readOnly,
    autoFocus,
    additionalProperties,
    pagesWithDetections,
  } = props;

  const { Id, Type, Path, InputType } = flatInvoiceDetail;
  const { addErrorField, removeErrorField } = React.useContext(ErrorCountContext);
  const { locale } = useLocale();
  const labelPath = `INVOICE_DETAIL.${Type}`;
  let autocompleteLabel: any;
  const [pastedValue, setPastedValue] = useState('');
  if (messages[labelPath]) {
    if (locale === 'de') {
      messages[labelPath].de
        ? (autocompleteLabel = messages[labelPath].de)
        : (autocompleteLabel = messages[labelPath].en);
    } else autocompleteLabel = messages[labelPath].en;
  } else autocompleteLabel = Type;

  autocompleteLabel = autocompleteLabel.toUpperCase();

  let scorePath = Path.slice(0, Path.length - 6);
  scorePath += '.Score';
  const form = useFormContext<FormValues>();
  const clientIdPath = 'Invoice.DetectInvoiceResponse.Client.Id';

  const pathCopy = Path.split('.');
  pathCopy.pop();
  const groupPath = pathCopy.join('.');

  useEffect(() => {
    if (InputType === 'number') {
      const formCopy = { ...form.values };
      const value = _.get(formCopy, Path);
      if (value) {
        _.set(formCopy, Path, convertToLocale(value.toString(), locale));

        form.plumb.next(
          {
            ...form.plumb.state,
            values: { ...formCopy },
          },
          FormTag.VALUES_CHANGE,
        );
      }
    }
  }, [locale]);

  const setBoundingBoxToZero = (obj: any, scorePath: any)=> {
    if(store.get('SetBBmMnual')) return;
    _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.X', 0);
    _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.Y', 0);
    _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.Width', 0);
    _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.Height', 0);
    pagesWithDetections.forEach((item: any) => {
      if(item.detections.find(((item: any) => item.Id === obj.Id)) !== undefined){
        item.detections.find(((item: any) => item.Id === obj.Id)).X = 0;
        item.detections.find(((item: any) => item.Id === obj.Id)).Y = 0;
        item.detections.find(((item: any) => item.Id === obj.Id)).Height = 0;
        item.detections.find(((item: any) => item.Id === obj.Id)).Width = 0;
      }
    })
  }

  const setNewBoundingBox = (obj: any, scorePath: any)=> {
    if(store.get('NoValue') && store.get('lassoActive') && store.get('SetBBmMnual')){
      const pageNumber = store.get('PageNumber');
      const scaleValue = store.get('scaleValue');
      let selectedItem : any = null;
      // setLassoActive(false);

      _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.X', Math.round(store.get('NoValueLassoLeft')));
      _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.Y', Math.round(store.get('NoValueLassoTop')));
      _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.Width', store.get('NoValueLassoWidth'));
      _.set(form.values, scorePath.substring(0, scorePath.length - 6) + '.Height', store.get('NoValueLassoHeight'));

      pagesWithDetections.forEach((item: any) => {
        if(item.detections.find(((item: any) => item.Id === obj.Id)) !== undefined){
            item.detections.find(((item: any) => item.Id === obj.Id)).X = store.get('NoValueLassoLeft') / scaleValue;
            item.detections.find(((item: any) => item.Id === obj.Id)).Y = store.get('NoValueLassoTop') / scaleValue;
            item.detections.find(((item: any) => item.Id === obj.Id)).Height = store.get('NoValueLassoHeight') / scaleValue;
            item.detections.find(((item: any) => item.Id === obj.Id)).Width = store.get('NoValueLassoWidth') / scaleValue;
        }
      })

      for (let i = 0; i < pagesWithDetections.length; i++) {
        pagesWithDetections[i].detections = pagesWithDetections[i].detections.filter((item : any)=> {
          if(item.Id === flatInvoiceDetail.Id) selectedItem = {...item}
          return item.Id !== flatInvoiceDetail.Id
        })
      }

      pagesWithDetections[pageNumber].detections.push(selectedItem);
    }
  }

  const insertLassoValue = (scorePath : any)=> {
    const lassoValue = store.get('LassoValue');
    const scaleValue = store.get('scaleValue');
    const pageNumber = store.get('PageNumber');
    const path = scorePath.substring(0, scorePath.length - 6);
    
    const croppedAreaTop =  Math.round(store.get('LassoTop') / scaleValue);
    const croppedAreaLeft =  Math.round(store.get('LassoLeft') / scaleValue);
    const croppedAreaHeight =  Math.round(store.get('LassoHeight') / scaleValue);
    const croppedAreaWidth =  Math.round(store.get('LassoWidth') / scaleValue);

    // setLassoActive(false);

    let selectedItem : any = null;
    if(lassoValue){
      _.set(form.values, path + '.X', croppedAreaLeft);
      _.set(form.values, path + '.Y', croppedAreaTop);
      _.set(form.values, path + '.Width', croppedAreaWidth);
      _.set(form.values, path + '.Height', croppedAreaHeight);
      _.set(form.values, path + '.Score', 1);

      for (let i = 0; i < pagesWithDetections.length; i++) {
        pagesWithDetections[i].detections = pagesWithDetections[i].detections.filter((item : any)=> {
          if(item.Id === flatInvoiceDetail.Id) selectedItem = {...item}
          return item.Id !== flatInvoiceDetail.Id
        })
      }
    }

    if(lassoValue){
      selectedItem!.X = croppedAreaLeft;
      selectedItem!.Y = croppedAreaTop;
      selectedItem!.Height = croppedAreaHeight;
      selectedItem!.Width = croppedAreaWidth;
      pagesWithDetections[pageNumber].detections.push(selectedItem);
    }
    store.set('LassoValue', '');
    store.set('NoValue', false);
  }

  // This is the path to the old sender value before it was expanded to be a group
  // When setting the sender name value/score we also need to set the value/score of this old value
  const oldSenderPath = 'Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions[19]';

  let threshold = 0;
  let priority = '';
  let canBeEmpty = false;
  //const conf = getConfigFromStore();
  const pathSeperated = Path.split('.');
  const nameWithDot = [];
  for (let i = 2; i < pathSeperated.length - 1; i++) nameWithDot.push(pathSeperated[i]);
  let namePath = nameWithDot.join('.');
  if (namePath[namePath.length - 1] === ']') namePath = namePath.slice(0, namePath.indexOf('['));
  //const invoiceDets = conf.InvoiceDetails;
    const invoiceDets = [
    {
      "Name": "DeliveryDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 8,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "GrandTotalAmount",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 16,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.967,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "InvoiceDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 64,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.489,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "NetTotalAmount",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 256,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "InvoiceId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 1024,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.011,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "DocumentType",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 8192,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0.798,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Iban",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 16384,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Bic",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 32768,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "InvoiceCurrency",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 524288,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0.413,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "DeliveryNoteId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 1048576,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "CustomerId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 2097152,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "UId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 8388608,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "SenderOrderId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 16777216,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "ReceiverOrderId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 33554432,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "SenderOrderDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 67108864,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "ReceiverVatId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 2048,
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "SenderVatId",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 512,
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "ReceiverOrderDate",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 134217728,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "VatTotalAmount",
      "PropertyPath": "InvoiceDetailTypePredictions",
      "InvoiceDetailTypeId": 1073741824,
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender",
      "PropertyPath": "Sender",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Sender.Name",
      "PropertyPath": "Sender.Name",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address",
      "PropertyPath": "Sender.Address",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Sender.Address.Street",
      "PropertyPath": "Sender.Address.Street",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address.ZipCode",
      "PropertyPath": "Sender.Address.ZipCode",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address.City",
      "PropertyPath": "Sender.Address.City",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Address.Country",
      "PropertyPath": "Sender.Address.Country",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.WebsiteUrl",
      "PropertyPath": "Sender.WebsiteUrl",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Email",
      "PropertyPath": "Sender.Email",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Phone",
      "PropertyPath": "Sender.Phone",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Sender.Fax",
      "PropertyPath": "Sender.Fax",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver",
      "PropertyPath": "Receiver",
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Receiver.Name",
      "PropertyPath": "Receiver.Name",
      "Priority": "NiceToHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": true,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address",
      "PropertyPath": "Receiver.Address",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": false
    },
    {
      "Name": "Receiver.Address.Street",
      "PropertyPath": "Receiver.Address.Street",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address.ZipCode",
      "PropertyPath": "Receiver.Address.ZipCode",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address.City",
      "PropertyPath": "Receiver.Address.City",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Address.Country",
      "PropertyPath": "Receiver.Address.Country",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.WebsiteUrl",
      "PropertyPath": "Receiver.WebsiteUrl",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Email",
      "PropertyPath": "Receiver.Email",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Phone",
      "PropertyPath": "Receiver.Phone",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "Receiver.Fax",
      "PropertyPath": "Receiver.Fax",
      "Priority": "NotNeeded",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": false,
      "HasScore": true
    },
    {
      "Name": "VatGroup",
      "PropertyPath": "PredictionGroups[0]",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": true,
      "IsQueryable": true,
      "HasScore": false
    },
    {
      "Name": "VatGroup.VatRate",
      "PropertyPath": "PredictionGroups[0].InvoiceDetailTypePredictions",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.95,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "VatGroup.VatAmount",
      "PropertyPath": "PredictionGroups[0].InvoiceDetailTypePredictions",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.95,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    },
    {
      "Name": "VatGroup.NetAmount",
      "PropertyPath": "PredictionGroups[0].InvoiceDetailTypePredictions",
      "Priority": "MustHave",
      "AutoProcessableThreshold": 0.95,
      "CanBeEmpty": false,
      "MultipleOccurrencesAllowed": false,
      "IsQueryable": true,
      "HasScore": true
    }
  ];
  let compareTo = '';
  if (nameWithDot[0] === 'Sender' || nameWithDot[0] === 'Receiver') compareTo = namePath;
  else {
    if (Type === 'VatRate' || Type === 'VatAmount' || Type === 'NetAmount') {
      compareTo = `VatGroup.${Type}`;
    } else if (Type === 'DueDateDuration' || Type === 'DueDateDate') {
      compareTo = `DueDateGroup.${Type}`;
    } else if (
      Type === 'DiscountPercent' ||
      Type === 'DiscountDate' ||
      Type === 'DiscountDuration'
    ) {
      compareTo = `DiscountGroup.${Type}`;
    } else if (Type === 'BankAccount' || Type === 'BankCode') {
      compareTo = `BankGroup.${Type}`;
    } else compareTo = Type;
  }
  invoiceDets.forEach((el) => {
    if (el.Name === compareTo) {
      threshold = el.AutoProcessableThreshold;
      priority = el.Priority;
      canBeEmpty = el.CanBeEmpty;
    }
  });

  const changeValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valCopy = form;
      _.set(valCopy.values, Path, e.target.value);
      if (Id === 'Sender-Name') {
        const oldSenderValuePath = oldSenderPath + '.Value';
        _.set(valCopy.values, oldSenderValuePath, e.target.value);
      }
      form.plumb.next(
        {
          ...form.plumb.state,
          values: { ...valCopy.values },
        },
        FormTag.VALUES_CHANGE,
      );
    },
    [form],
  );

  const setScoreToOne = useCallback(() => {
    const valCopy = form;
    if (Id === 'Sender-Name') {
      const oldSenderScorePath = oldSenderPath + '.Score';
      _.set(valCopy.values, oldSenderScorePath, 1);
    }
    _.set(valCopy.values, scorePath, 1);
    onThreshold(Id, true);
    if (priority === 'MustHave' && refs[Id] && refs[Id].current)
      (refs[Id]!.current! as HTMLElement).style.borderColor = '#92D050';
    form.plumb.next(
      {
        ...form.plumb.state,
        values: { ...valCopy.values },
      },
      FormTag.VALUES_CHANGE,
    );
  }, [form]);

  const setSupplierScoreToOne = useCallback(() => {
    const groupName = Id.split('-')[0];
    if (groupName === 'Supplier') {
      const supplierScore = form.values.Invoice.SupplierResult.Score;
      if (supplierScore !== 1 && form.values.Invoice.SupplierResult.SupplierId) {
        const valCopy = form;
        valCopy.values.Invoice.SupplierResult.Score = 1;
        //if (conf.MustHaveSupplierInValidation)
        if (true)
          document.getElementById('Supplier')!.style.borderColor = '#92D050';
        form.plumb.next(
          {
            ...form.plumb.state,
            values: { ...valCopy.values },
          },
          FormTag.VALUES_CHANGE,
        );
      }
    }
  }, [form]);

  const changeSelectValue = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const valCopy = form;
      _.set(valCopy.values, Path, e.target.options[e.target.selectedIndex].value);
      form.plumb.next(
        {
          ...form.plumb.state,
          values: { ...valCopy.values },
        },
        FormTag.VALUES_CHANGE,
      );
    },
    [form],
  );

  const changeDate = useCallback(
    (d, _event: React.SyntheticEvent<any, Event>) => {
      const valCopy = form;
      _.set(valCopy.values, Path, d);
      form.plumb.next(
        {
          ...form.plumb.state,
          values: { ...valCopy.values },
        },
        FormTag.VALUES_CHANGE,
      );
      forceReRender();
    },
    [form],
  );

  const idSplit = Id.split('-');
  let columnName = '';
  if (idSplit.length === 3) columnName = idSplit[1];
  else columnName = idSplit[idSplit.length - 1];

  const changeFieldGroup = useCallback(
    (s: DetectedSupplier | DetectedClient | null) => {
      const groupSplit = groupPath.split('.');
      if (!s) return;
      const formCopy = { ...form.values };
      _.set(formCopy, groupPath, s);
      if (groupSplit[2] === 'Client') {
        if(_.get(formCopy, "Invoice.DetectInvoiceResponse.Client.Id")){
          _.set(formCopy, "Invoice.DetectInvoiceResponse.AdditionalProperties.ClientId", _.get(formCopy, "Invoice.DetectInvoiceResponse.Client.Id")); 
        }
        groupSplit.pop();
        groupSplit.push('Supplier');
        const newGroup = groupSplit.join('.');
        _.set(formCopy, newGroup, {
          SupplierId: '',
          Name: '',
          Uids: [''],
          Ibans: [''],
          City: '',
          Country: '',
          Street: '',
          ZipCode: '',
        });
        formCopy.BadClientId = undefined;
        const badClientIdP = document.getElementById('badClientId');
        if (badClientIdP) {
          badClientIdP.style.display = 'none';
        }
        formCopy.BadSupplierId = undefined;
        const badSupplierIdp = document.getElementById('badSupplierId');
        if (badSupplierIdp) {
          badSupplierIdp.style.display = 'none';
        }
        formCopy.Invoice.SupplierResult.SupplierId = '';
        //if (conf.IsSupplierVisible && conf.MustHaveSupplierInValidation)
        if (true)
          document.getElementById('Supplier')!.style.borderColor = 'red';
        //if (conf.IsClientVisible && conf.MustHaveClientInValidation)
        if (true)
          document.getElementById('Client')!.style.borderColor = '#92D050';
      } else {
        formCopy.Invoice.SupplierResult.SupplierId = (s as DetectedSupplier).SupplierId;
        formCopy.Invoice.SupplierResult.Score = 1;
        formCopy.BadSupplierId = undefined;
        const badSupplierIdp = document.getElementById('badSupplierId');
        if (badSupplierIdp) {
          badSupplierIdp.style.display = 'none';
        }
        //if (conf.IsSupplierVisible && conf.MustHaveSupplierInValidation)
        if (true)
          document.getElementById('Supplier')!.style.borderColor = '#92D050';
      }
      form.plumb.next(
        {
          ...form.plumb.state,
          values: { ...formCopy },
        },
        FormTag.VALUES_CHANGE,
      );
    },
    [form],
  );

  switch (InputType) {
    case 'invoice-or-creditmemo':
      return (
        <FormSelectField path={Path}>
          {({ selectProps }) => {
            {(_.get(form.values, scorePath) !== 1) && disabled && setScoreToOne()}
            return (
            <div className="formFieldWrapper">
            <div className="formFieldLabel">{<FormattedMessage defaultMessage={Type} id={`INVOICE_DETAIL.${Type}`} />}</div>
            <Select
              {...selectProps}
              onChange={changeSelectValue}
              style={{
                borderColor:
                  priority === 'MustHave'
                    ? _.get(form.values, scorePath) > threshold
                      ? '#92D050'
                      : '#FCE300'
                    : '1px solid rgb(205, 205, 205)',
                backgroundColor: 
                  priority === 'MustHave'
                    ? _.get(form.values, scorePath) > threshold
                      ? '#F3FFE5'
                      : '#FFFDEB'
                    : 'transparent',
              }}
              tabIndex={1}
              autoFocus={autoFocus}
              disabled={disabled}
              onFocus={setScoreToOne}
            >
              <FormattedMessage id="INVOICE_DETAIL.VALUE.INVOICE">
                {(text) => <option value="Invoice">{text}</option>}
              </FormattedMessage>
              <FormattedMessage id="INVOICE_DETAIL.VALUE.CREDIT_MEMO">
                {(text) => <option value="CreditMemo">{text}</option>}
              </FormattedMessage>
            </Select>
            </div>
          )}}
        </FormSelectField>
      );
    case 'number':
      return (
        <FormInputField path={Path}>
          {({ inputProps }) => {
          (_.get(form.values, scorePath) !== 1) && disabled && setScoreToOne()
          return (
            <div className="formFieldWrapper">
            <div className="formFieldLabel">{<FormattedMessage defaultMessage={Type} id={`INVOICE_DETAIL.${Type}`} />}</div>
            <TextField
              {...inputProps}
              tabIndex={1}
              ref={(el) => {
                inputProps.ref(el);
                refs[Id].current = el;
              }}
              autoFocus={autoFocus}
              style={{
                borderColor:
                  priority === 'MustHave'
                    ? _.get(form.values, scorePath) > threshold
                      ? '#92D050'
                      : canBeEmpty
                      ? '#FCE300'
                      : _.get(form.values, Path) === ''
                      ? 'red'
                      : '#FCE300'
                    : '1px solid rgb(205, 205, 205)',
                  backgroundColor: 
                    priority === 'MustHave'
                      ? _.get(form.values, scorePath) > threshold
                        ? '#F3FFE5'
                        : canBeEmpty
                        ? '#FFFDEB'
                        : _.get(form.values, Path) === ''
                        ? 'rgba(255, 0, 0, 0.1)'
                        : '#FFFDEB'
                      : 'transparent',
              }}
              disabled={disabled}
              onFocus={()=> {
                onHighlightDetection(flatInvoiceDetail);
                setScoreToOne();
                setNewBoundingBox(flatInvoiceDetail, scorePath);
              }}
              onBlur={() => {
                onHighlightDetection(null);
                store.set('SetBBmMnual', false);
              }}
              onChange={(e) => {
                if (pastedValue) {
                  e.target.value = convertToLocale(pastedValue, locale);
                }
                if (containsValidNumberCharacters(e.target.value)) {
                  inputProps.onChange(e);
                  if (refs[Id] && refs[Id].current) {
                    if (!validateNumber(e.target.value)) {
                      addErrorField(Id);
                      (refs[Id]!.current! as HTMLElement).classList.add('red-border');
                    } else {
                      removeErrorField(Id);
                      (refs[Id]!.current! as HTMLElement).classList.remove('red-border');
                    }
                  }
                }
                setPastedValue('');
              }}
              onKeyDown={(event)=>{
                if((event.keyCode >= 48 && event.keyCode <= 90) || event.keyCode === 8) {
                  setBoundingBoxToZero(flatInvoiceDetail, scorePath);
                }
              }}
              onPaste={(e) => {
                setPastedValue(e.clipboardData.getData('Text'));
                store.get('LassoValue') && insertLassoValue(scorePath);
                setScoreToOne();
              }}
              className="customInputField"
            /> 
            </div>
          )}}
        </FormInputField>
      );
    case 'date':
      return (
        <FormDateField path={Path}>
          {({ DateInput }) => {
            return (
            <div className="formFieldWrapper">
            <div className="formFieldLabel">{<FormattedMessage defaultMessage={Type} id={`INVOICE_DETAIL.${Type}`} />}</div>
            <DateInput
              tabIndex={1}
              inputRef={(el) => (refs[Id].current = el)}
              customInput={
                <TextField
                  style={{
                    borderColor:
                      priority === 'MustHave'
                        ? _.get(form.values, scorePath) > threshold
                          ? '#92D050'
                          : canBeEmpty
                          ? '#FCE300'
                          : _.get(form.values, Path) === ''
                          ? 'red'
                          : '#FCE300'
                        : '1px solid rgb(205, 205, 205)',
                      backgroundColor: 
                        priority === 'MustHave'
                          ? _.get(form.values, scorePath) > threshold
                            ? '#F3FFE5'
                            : canBeEmpty
                            ? '#FFFDEB'
                            : _.get(form.values, Path) === ''
                            ? 'rgba(255, 0, 0, 0.1)'
                            : '#FFFDEB'
                          : 'transparent',
                  }}
                  className="customInputField"
                />
              }
              autoFocus={autoFocus}
              disabled={disabled}
              onFocus={()=> {
                onHighlightDetection(flatInvoiceDetail);
                setScoreToOne();
                setNewBoundingBox(flatInvoiceDetail, scorePath)
              }}
              onBlur={() => {
                onHighlightDetection(null);
                store.set('SetBBmMnual', false);
              }}
              onSelect={changeDate && (()=> setBoundingBoxToZero(flatInvoiceDetail, scorePath))}
            />
            </div>
          )}}
        </FormDateField>
      );
    case 'autocomplete':
      return (
        <FormInputField path={Path}>
          {({ inputProps }) => {
            const el = document.getElementById(Id);
            let hideField = true;
            let hideExtraFields = false;
            const groupSplit = groupPath.split('.');
            if (groupSplit[2] === 'Supplier' || groupSplit[2] === 'Client') {
              const groupVals = _.get(form.values, groupPath);
              const keys = Object.keys(groupVals);
              for (let i = 0; i < keys.length; i++) {
                const element = keys[i];
                if (element !== columnName) {
                  if (!groupVals[element] && !_.get(form.values, Path)) {
                    hideField = false;
                    const indexNumber = Number(Path[Path.length - 2]);
                    if (indexNumber && indexNumber > 0) hideExtraFields = true;
                  }
                  break;
                }
              }
            }
            if (!inputProps.value && (hideField || hideExtraFields)) {
              if (el) el.style.display = 'none';
              return null;
            } else {
              if (el) el.style.display = 'block';
              return (
                <div className="formFieldWrapper">
                <div className="formFieldLabel">{autocompleteLabel}</div>
                <InvoiceAutocomplete
                  label=''
                  autoFocus={autoFocus}
                  minCharacters={3}
                  isSupplier={groupSplit[2] === 'Supplier'}
                  onFocus={setSupplierScoreToOne}
                  value={_.get(form.values, groupPath)}
                  tabIndex={1}
                  onValueChange={changeFieldGroup}
                  getData={Id.split('-')[0] === 'Client' ? getClients : getSuppliers}
                  clientId={_.get(form.values, clientIdPath)}
                  columnName={columnName}
                  fieldName={Path.split('.').slice(-1)[0]}
                />
                </div>
              );
            }
          }}
        </FormInputField>
      );
    default:
      return (
        <FormInputField path={Path}>
          {({ inputProps }) => {
            if(additionalProperties) {
              return (
                <div className="additionalDetailField">
                  <p className="additionalDetailTitle">
                    <FormattedMessage defaultMessage={Type} id={`INVOICE_DETAIL.${Type}`} />:
                  </p>
                  <p className="additionalDetailText">{inputProps.value}</p>
                </div>

              )
            } 
            return (
            <div className="formFieldWrapper">
            <div className="formFieldLabel">{<FormattedMessage defaultMessage={Type} id={`INVOICE_DETAIL.${Type}`} />}</div>
            <TextField 
              tabIndex={1}
              autoFocus={autoFocus}
              style={{
                borderColor:
                  priority === 'MustHave'
                    ? _.get(form.values, scorePath) > threshold
                      ? '#92D050'
                      : canBeEmpty
                      ? '#FCE300'
                      : _.get(form.values, Path) === ''
                      ? 'red'
                      : '#FCE300'
                    : '1px solid rgb(205, 205, 205)',
                  backgroundColor: 
                    priority === 'MustHave'
                      ? _.get(form.values, scorePath) > threshold
                        ? '#F3FFE5'
                        : canBeEmpty
                        ? '#FFFDEB'
                        : _.get(form.values, Path) === ''
                        ? 'rgba(255, 0, 0, 0.1)'
                        : '#FFFDEB'
                      : 'transparent',
              }}
              {...inputProps}
              ref={(el) => (refs[Id].current = el)}
              disabled={disabled}
              onFocus={()=> {
                if (!readOnly) {
                  onHighlightDetection(flatInvoiceDetail);
                  setScoreToOne();
                  setNewBoundingBox(flatInvoiceDetail, scorePath);
                }
              }}
              readOnly={readOnly}
              onKeyDown={(event)=>{
                if((event.keyCode >= 48 && event.keyCode <= 90) || event.keyCode === 8) {
                  setBoundingBoxToZero(flatInvoiceDetail, scorePath);
                }
              }}

              onChange={(el)=>{
                {!readOnly && changeValue(el)}
              }}
              onBlur={() => {
                onHighlightDetection(null);
                store.set('SetBBmMnual', false);                
              }}
              onPaste={() => {
                store.get('LassoValue') && insertLassoValue(scorePath);
                setScoreToOne();
              }}
              className="customInputField"
            />
            </div>
          )}}
        </FormInputField>
      );
  }
};

const ComposedInvoiceDetailDetectionsFormField = React.memo(InvoiceDetailDetectionsFormField);
export { ComposedInvoiceDetailDetectionsFormField as InvoiceDetailDetectionsFormField };
